import {
  CardBackground,
  ToastProps,
  Typography,
  useToast,
} from "@hellocarbo/lasagne";
import { differenceInMonths, formatISO } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import {
  Confirmation,
  DateAndDuration,
  DURATION_IN_MONTHS,
} from "@/components/CreateTrackingCampaign";
import PageWrapper from "@/components/ui/PageWrapper";
import { TCampaignDuration, TTrackingCampaign } from "@/types";
import {
  calculateNextCampaignEndDate,
  calculateNextCampaignStartDate,
} from "@/utils/formatDate";

export async function loader() {
  const response = await fetch("/api/client/tracking_campaigns/latest");
  if (response.status !== 200) {
    throw data("Failed to load the latest tracking campaign", {
      status: response.status,
    });
  }

  const { data: latestTrackingCampaignDates } = (await response.json()) as {
    data: TTrackingCampaign;
  };

  // Calculate the month difference
  const monthsDiff = differenceInMonths(
    latestTrackingCampaignDates.period_end,
    latestTrackingCampaignDates.period_start
  );

  // Set default duration to 12 months
  let lastCampaignDuration = TCampaignDuration.twelveMonths;

  if (monthsDiff === 3) {
    lastCampaignDuration = TCampaignDuration.threeMonths;
  } else if (monthsDiff === 6) {
    lastCampaignDuration = TCampaignDuration.sixMonths;
  }

  return {
    lastCampaignPeriodEnd: latestTrackingCampaignDates.period_end,
    lastCampaignDuration,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const startDate = new Date(formData.get("startDate") as string);
  const duration = formData.get("duration") as TCampaignDuration;

  // Calculate end date (start date + duration in months)
  const endDate = formatISO(
    calculateNextCampaignEndDate(startDate, DURATION_IN_MONTHS[duration])
  );

  const response = await fetch("/api/client/tracking_campaigns/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ period_start: startDate, period_end: endDate }),
  });

  if (response.status !== 201) {
    // This error returns a html document and cannot be parsed as JSON
    if (response.status === 500) {
      return { error: undefined, toastError: "trackingCampaign:error:generic" };
    }

    const errorData = (await response.json()) as { code: string };

    if (errorData?.code === "period_intersects_existing_tracking_campaign") {
      return {
        error: "trackingCampaign:error.periodsIntersect",
        toastError: undefined,
      };
    }

    return { error: undefined, toastError: "trackingCampaign:error:generic" };
  }

  const {
    meta: { redirect_url: redirectUrl },
    data: { id },
  } = (await response.json()) as {
    meta: { redirect_url: string };
    data: { id: number };
  };

  return { ok: true, redirectUrl, id };
}

function CreateTrackingCampaign() {
  const { t } = useTranslation("trackingCampaign");
  const { toast } = useToast();
  const navigation = useNavigation();
  const [error, setError] = useState<string>();

  const { lastCampaignPeriodEnd, lastCampaignDuration } =
    useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();

  invariant(
    lastCampaignPeriodEnd && lastCampaignDuration,
    "Unable to access information from the latest campaign."
  );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [startDate, setStartDate] = useState<Date>(
    calculateNextCampaignStartDate(lastCampaignPeriodEnd)
  );
  const [duration, setDuration] =
    useState<TCampaignDuration>(lastCampaignDuration);

  useEffect(() => {
    if (navigation.state === "idle") {
      // Show generic error message in negative toast
      if (actionData?.toastError) {
        toast({
          title: t(actionData.toastError),
          intent: "negative",
        });
      } else if (actionData?.ok) {
        const { id, redirectUrl } = actionData;

        // Store new tracking campaign id in local storage
        localStorage.setItem("currentTrackingCampaign", `${id}`);

        // Add toast data to session storage (see Root.tsx for toast display logic)
        sessionStorage.setItem(
          "toast",
          JSON.stringify({
            title: t("trackingCampaign:newFootprintCreated"),
            intent: "positive",
          } as ToastProps)
        );

        // Redirect to redirectUrl
        window.location.assign(redirectUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData, navigation.state]);

  useEffect(() => {
    // Reset error when the confirmation view is not displayed
    if (error && !showConfirmation) {
      setError(undefined);
    }

    // Set an error when the confirmation view is displayed and the action has been submitted
    if (
      showConfirmation &&
      navigation.state === "loading" &&
      actionData?.error
    ) {
      setError(actionData.error);
    }
  }, [actionData?.error, navigation.state, showConfirmation, error]);

  return (
    <PageWrapper>
      <div className="my-lg grid grid-cols-4">
        <CardBackground className="col-start-2 col-end-4">
          <Typography as="h1" step="2" weight="bold" className="mb-xs">
            {t("trackingCampaign:startFootprint")}
          </Typography>
          <Form method="POST">
            <div className={showConfirmation ? "hidden" : ""}>
              <DateAndDuration
                prevCampaignEndDate={new Date(lastCampaignPeriodEnd)}
                duration={duration}
                onDateChange={setStartDate}
                onDurationChange={setDuration}
                onButtonClick={() => setShowConfirmation(true)}
              />
            </div>
            {showConfirmation && (
              <Confirmation
                startDate={startDate}
                duration={duration}
                loading={navigation.state !== "idle"}
                onBackButtonClick={() => setShowConfirmation(false)}
                errorMessage={error}
              />
            )}
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default CreateTrackingCampaign;
